import { useCallback, useState } from 'react'
import BackofficePanel, { PanelError } from '../BackofficePanel'
import { relativeTime } from '../feedText'
import { useAdminPoll } from '../../../app/backoffice/useAdminPoll'
import { getAdminActions } from '../../../api/admin'

/**
 * The audit log.
 *
 * It belongs **here**, at the foot of the controls that write to it, where the consequence of the
 * button you are about to press is visible. Who, what, when, before → after, and the mode it
 * happened in — that last one because six months later "why is there a manual 3-1 on this fixture"
 * is answered completely differently depending on whether it was set against the replica or
 * against real football.
 */

const AUDIT_POLL_MS = 30_000

export default function AuditPanel({ enabled }: { enabled: boolean }) {
  const [action, setAction] = useState<string>('all')
  const load = useCallback(
    () => getAdminActions({ limit: 50, action: action === 'all' ? undefined : action }),
    [action]
  )
  const poll = useAdminPoll(load, { enabled, intervalMs: AUDIT_POLL_MS })
  // Relative times are measured from when the payload arrived, not from a clock read during
  // render — the panel only changes when a poll lands, so the fetch instant is both the honest
  // reference point and a pure one. Nothing below renders before the first poll.
  const nowMs = poll.receivedAtMs ?? 0

  return (
    <BackofficePanel
      title="Admin actions"
      badge={poll.data ? String(poll.data.actions.length) : '…'}
      defaultOpen={false}
      subtitle="Every mutating admin action, written in the same transaction as the change it describes."
    >
      {poll.error !== null && <PanelError error={poll.error} />}
      {poll.data && (
        <>
          <div className="bo-filters">
            <select value={action} onChange={(e) => setAction(e.target.value)} aria-label="Action">
              <option value="all">All actions</option>
              {poll.data.actionNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          {poll.data.actions.length === 0 ? (
            <p className="bo-empty">Nothing has been done from here yet.</p>
          ) : (
            <div className="bo-list">
              {poll.data.actions.map((entry) => (
                <div key={entry.id} className="bo-feed-row">
                  <span className="bo-feed-row__time" title={entry.createdAt}>
                    {relativeTime(entry.createdAt, nowMs)}
                  </span>
                  <span className="bo-feed-row__text">
                    <strong>{entry.action}</strong>
                    {entry.target && <span className="bo-mono"> {entry.target}</span>}
                    <br />
                    {entry.actorEmail}
                    {entry.reason && ` — ${entry.reason}`}
                    {/* The before/after is what makes this answer "what did this do" rather than
                        "something happened". Rendered compactly; the whole payload is on hover. */}
                    {(entry.before !== null || entry.after !== null) && (
                      <span
                        className="bo-mono"
                        title={`before: ${JSON.stringify(entry.before)}\nafter: ${JSON.stringify(entry.after)}`}
                      >
                        {' '}
                        {summarise(entry.before)} → {summarise(entry.after)}
                      </span>
                    )}
                  </span>
                  <span className="bo-tag">{entry.mode}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </BackofficePanel>
  )
}

/** One line's worth of a JSON payload — the full thing is on the row's `title`. */
function summarise(value: unknown): string {
  if (value === null || value === undefined) return '∅'
  const text = JSON.stringify(value)
  return text.length > 60 ? `${text.slice(0, 57)}…` : text
}
